import { oneOf, array } from "decoders";
import { uniqueId } from "lodash";
import type { FormState } from "react-hook-form";

import { LeadAttributionMetadata } from "@brexhq/next/types/brex-api";
import {
  CHILI_PIPER_ECOMM_PROGRAM_TREATMENT,
  CHILI_PIPER_ECOMM_PROGRAM_CONTROL,
} from "@brexhq/shared/types/LeadForm";

import { DynamicLeadFormController } from "./dynamicLeadFormController";
import { useLeadFormFields } from "./hooks";

export interface FieldValues {
  workEmail: string;
  firstName: string;
  lastName: string;
  mobileNumber: string;
  website: string;
  companyName: string;
  productsOfInterest: string[];
  employeeCount: string;
  monthlyCreditSpend: string;
  monthlySales: string;
  isEcomm: string;
  fundingSource: string;
  howDidYouHearAboutUs: string;
}

export enum FormSubmissionState {
  NOT_SUBMITTED = "not_submitted",
  SUBMISSION_ATTEMPTED = "submission_attempted",
  SUBMITTED_AND_FAILED = "submitted_and_failed",
  SUBMITTED_AND_SUCCEEDED = "submitted_and_succeeded",
}

export enum LeadRoutingEventType {
  ROUTING_SCRIPT_LOADING_FAILED = "routing script loading failed",
  LEAD_ROUTING_INITIATED = "lead routing initiated",
  QUALIFIED_LEAD_AND_A_MEETING_IS_BOOKED = "qualified lead and a meeting is booked",
  QUALIFIED_LEAD_AND_A_MEETING_IS_NOT_BOOKED = "qualified lead and a meeting is not booked",
  DISQUALIFIED_LEAD = "disqualified lead",
  DEFAULT = "default",
}

export enum LeadFormSubmissionStatus {
  COMPLETE = "Complete",
  INCOMPLETE = "Incomplete",
}

export enum ScriptLoadingStatus {
  NOT_STARTED = "not_started",
  LOADING = "loading",
  SUCCESSFULLY_LOADED = "successfully_loaded",
  TIMED_OUT = "timed_out",
  ERROR = "error",
}

export const creditSpendOptions = [
  {
    label: "<$75K",
    value: "<$75K",
  },
  {
    label: "$75k - $500K",
    value: "$75k - $500K",
  },
  {
    label: "$500K-1M",
    value: "$500K-1M",
  },
  {
    label: "$1M+",
    value: "$1M+",
  },
] as const;

export const decodeCreditSpendOptionValue = oneOf(
  creditSpendOptions.map((option) => option.value),
);

export type CreditSpendOptionValue = typeof creditSpendOptions[number]["value"];

export const employeeCountOptions = [
  {
    label: "1 - 4",
    value: "1 - 4",
  },
  {
    label: "5 - 20",
    value: "5 - 20",
  },
  {
    label: "21 - 50",
    value: "21 - 50",
  },
  {
    label: "51 - 100",
    value: "51 - 100",
  },
  {
    label: "101 - 500",
    value: "101 - 500",
  },
  {
    label: "501 - 2,500",
    value: "501 - 2,500",
  },
  {
    label: "2,500+",
    value: "2,500+",
  },
] as const;

export const decodeEmployeeCountOptionValue = oneOf(
  employeeCountOptions.map((option) => option.value),
);

export type EmployeeCountOptionValue =
  typeof employeeCountOptions[number]["value"];

export const fundingSourceOptions = [
  {
    label: "Venture Capital / Angel Investor / Accelerator",
    value: "professional_investor",
  },
  {
    label: "Crowdsourced",
    value: "Crowdsourced",
  },
  {
    label: "Initial Coin Offering",
    value: "Initial Coin Offering",
  },
  {
    label: "Family",
    value: "Family",
  },
  {
    label: "Self Funded",
    value: "Self Funded",
  },
] as const;

export const decodeFundingSourceOptionValue = oneOf(
  fundingSourceOptions.map((option) => option.value),
);

export type FundingSourceOptionValue = ReturnType<
  typeof decodeFundingSourceOptionValue["verify"]
>;

export const productInterestOptions = [
  {
    key: uniqueId(),
    name: "Business Account",
    label: "Business account",
    description: "Send free wires worldwide and get FDIC coverage.",
  },
  {
    key: uniqueId(),
    name: "Corporate Card",
    label: "Corporate card",
    description: "Get 10-20x higher limits with no personal guarantee.",
  },
  {
    key: uniqueId(),
    name: "Spend Management Software",
    label: "Spend management software",
    description: "Manage company spend while maximizing speed.",
  },
] as const;

const [first, second, third] = productInterestOptions;

export const productInterestOptionsVariant = [
  first,
  second,
  {
    ...third,
    key: uniqueId(),
    label: "Spend management on Empower",
    description: "Manage spend & increase speed.",
  },
];

export const decodeProductInterestOptionValue = oneOf(
  productInterestOptions.map((option) => option.name),
);

export type ProductInterestOptionValue = ReturnType<
  typeof decodeProductInterestOptionValue["verify"]
>;

export const decodeProductInterestSelection = array(
  decodeProductInterestOptionValue,
);

export const EMPOWER_MULTI_STEP_LEAD_FORM = "Empower multi-step lead form";
export const FORM_ID = 1646;

export type ProgramName =
  | typeof CHILI_PIPER_ECOMM_PROGRAM_TREATMENT
  | typeof CHILI_PIPER_ECOMM_PROGRAM_CONTROL
  | string;

export const CONTROL = "CONTROL";
export const TREATMENT = "TREATMENT";
export const HOW_DID_YOU_HEAR_ABOUT_US_MAX_LENGTH = 100;
export const PLEASE_USE_WORK_EMAIL_MESSAGE = "Please use your work email.";
export const PLEASE_USE_A_VALID_WEBSITE_MESSAGE =
  "Please use a valid website URL.";
export const INVALID_INPUT_MESSAGE = "Invalid input.";
export const BACK_BUTTON_TEST_ID = "BACK_BUTTON";
export const SUBMIT_BUTTON_TEST_ID = "SUBMIT_BUTTON";

export const ERROR_BOX_MESSAGE =
  "Oops! Something went wrong here — please try again. If the error continues please reach out to support.";

export type Fields = ReturnType<typeof useLeadFormFields>["fields"];

export interface Step {
  /**
   * Unique step identifier
   */
  id: string;
  view: React.FC<{ ctrl: DynamicLeadFormController }>;
  isValid: (formState: FormState<FieldValues>) => boolean;
  hasPrevStep: (fields: Fields) => boolean;

  /**
   * Invoked when a user clicks submit/next and the form has no errors
   */
  next: (actions: {
    /**
     * Creates or updates a lead with current form values and pending status
     */
    createOrUpdateLead: () => Promise<void>;

    /**
     * Set which step should be active
     */
    setStep: (step: Step) => void;

    /**
     * Mark form as submitted, update lead with current form values and "completed"
     * status, and show ChiliPiper if approapriate
     */
    finishWithUpdate: () => Promise<void>;

    /**
     * Mark form as submitted, update lead with current form values and
     * "completed" status, and show waitlist screen
     */
    finishWithWaitList: () => Promise<void>;

    /**
     * Mark form as submitted, update lead with current form values and
     * "completed" status, and show signup (ssu form) screen
     */
    finishWithSSU: () => Promise<void>;

    /**
     * Keeps track of errors, dirty fields, etc
     */
    formState: FormState<FieldValues>;

    /**
     * Keeps track of values and handlers
     */
    fields: Fields;
  }) => Promise<void>;

  /**
   * Invoked when user clicks back
   */
  back: (setStep: (step: Step) => void) => void;
}

const stepNames = {
  Basics: "basics",
  ProductsOfInterest: "productsOfInterest",
} as const;

export const stepOrder = [
  stepNames.Basics,
  stepNames.ProductsOfInterest,
] as const;

export interface FormMetaProps {
  customProgramName: string;
  showCustomLeftContent: boolean;
  customFormTitle?: string;
  customSubmitButtonLabel?: string;
  bypassSuccessContentUrl?: {
    to: string | null;
    toExternal: string | null;
    noFollow?: boolean | null;
  };
  hiddenFields?: Pick<
    LeadAttributionMetadata,
    | "contentType"
    | "contentTag"
    | "webinarId"
    | "videoId"
    | "eventId"
    | "pageSegment"
    | "brexSolution"
    | "brexFeature"
    | "salesforceCampaign"
  >;
}
